import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Lock, Eye, Database, UserCheck, Mail } from 'lucide-react';

export default function PrivacyPolicy({ onGoHome }) {
  const sections = [
    {
      icon: Database,
      title: "Information We Collect",
      content: [
        "Personal identification information (name, email address, phone number)",
        "Business information related to your commercial financing needs",
        "Financial information necessary for mortgage applications",
        "Communication records between you and our team",
        "Website usage data through cookies and analytics"
      ]
    },
    {
      icon: Eye,
      title: "How We Use Your Information",
      content: [
        "To process and evaluate your commercial mortgage applications",
        "To communicate with you about your financing options",
        "To provide personalized service and recommendations",
        "To improve our website and services",
        "To comply with legal and regulatory requirements"
      ]
    },
    {
      icon: Shield,
      title: "Information Protection",
      content: [
        "We implement industry-standard security measures to protect your data",
        "All sensitive information is encrypted during transmission",
        "Access to personal information is restricted to authorized personnel only",
        "We regularly review and update our security practices",
        "We never sell your personal information to third parties"
      ]
    },
    {
      icon: UserCheck,
      title: "Your Rights",
      content: [
        "Access your personal information we hold",
        "Request correction of inaccurate data",
        "Request deletion of your personal information",
        "Opt-out of marketing communications",
        "Lodge a complaint with a supervisory authority"
      ]
    },
    {
      icon: Lock,
      title: "Data Retention",
      content: [
        "We retain your information only as long as necessary for the purposes outlined",
        "Financial records are kept in accordance with regulatory requirements",
        "You may request deletion of your data at any time",
        "Some information may be retained for legal compliance purposes"
      ]
    }
  ];

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <div className="bg-slate-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button 
            onClick={onGoHome}
            className="inline-flex items-center text-emerald-400 hover:text-emerald-300 mb-8 group"
          >
            <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </button>
          <motion.h1 
            className="text-4xl md:text-5xl font-bold mb-4"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Privacy Policy
          </motion.h1>
          <motion.p 
            className="text-slate-400 text-lg"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Last updated: December 2024
          </motion.p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          className="prose prose-lg max-w-none"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <p className="text-slate-600 text-lg leading-relaxed mb-12">
            At Done Commercials, we are committed to protecting your privacy and ensuring the security 
            of your personal information. This Privacy Policy explains how we collect, use, disclose, 
            and safeguard your information when you visit our website or use our commercial mortgage services.
          </p>

          {sections.map((section, index) => (
            <motion.div
              key={section.title}
              className="mb-12 bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 + index * 0.1 }}
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mr-4">
                  <section.icon className="w-6 h-6 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">{section.title}</h2>
              </div>
              <ul className="space-y-3">
                {section.content.map((item, i) => (
                  <li key={i} className="flex items-start text-slate-600">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}

          {/* Cookies Section */}
          <motion.div
            className="mb-12 bg-white rounded-2xl p-8 shadow-sm border border-slate-100"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1 }}
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Cookies & Tracking</h2>
            <p className="text-slate-600 mb-4">
              We use cookies and similar tracking technologies to enhance your browsing experience, 
              analyze website traffic, and understand where our visitors come from. You can control 
              cookie preferences through your browser settings.
            </p>
            <p className="text-slate-600">
              Third-party services we use may also collect information about your online activities 
              over time and across different websites.
            </p>
          </motion.div>

          {/* Contact Section */}
          <motion.div
            className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-8 text-white"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.1 }}
          >
            <div className="flex items-center mb-4">
              <Mail className="w-6 h-6 mr-3" />
              <h2 className="text-2xl font-bold">Questions About Privacy?</h2>
            </div>
            <p className="mb-6 text-emerald-50">
              If you have any questions about this Privacy Policy or our data practices, 
              please don't hesitate to contact us.
            </p>
            <a 
              href="mailto:info@donecommercial.ca"
              className="inline-flex items-center bg-white text-emerald-600 px-6 py-3 rounded-lg font-semibold hover:bg-emerald-50 transition-colors"
            >
              Contact Us
            </a>
          </motion.div>
        </motion.div>
      </div>

      {/* Simple Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-slate-400">
            &copy; 2025 Done Commercials. All rights reserved.
          </p>
          <div className="mt-4 space-x-6">
            <a href="#privacy-policy" className="text-emerald-400 hover:text-emerald-300">Privacy Policy</a>
            <a href="#terms-conditions" className="text-slate-400 hover:text-emerald-400">Terms & Conditions</a>
          </div>
        </div>
      </footer>
    </motion.div>
  );
}
